import React, { useEffect, useState } from 'react'
import { View } from 'react-native'
import { Link, useLocation } from 'react-router'
import styled from 'styled-components'
import { COLORS } from '@utils/colors'
import { Links } from '@utils/links'
import { SubMenuProps } from '@Interfaces/Component/SubMenuInterface'

const SidebarLink = styled(Link)<{ active?: number; disabled?: boolean }>`
  display: flex;
  color: ${({ disabled, active }) =>
    disabled
      ? COLORS.primary.neutral_600
      : active
        ? COLORS.primary.neutral
        : COLORS.primary.darkBlue};
  justify-content: space-between;
  align-items: center;
  min-height: 62px;
  padding: 22px 20px 22px 15px;
  list-style: none;
  text-decoration: none;
  background: ${({ active }) => (active ? COLORS.primary.darkBlue : COLORS.primary.white)};
  border-left: ${({ active }) => (active ? '3px solid ' + COLORS.primary.lightBlue : '')};
  border-bottom: 2px solid ${COLORS.primary.offWhite};
  box-sizing: border-box;

  ${({ disabled }) => {
    if (disabled) {
      return `&:hover {
      cursor: not-allowed;
      }`
    }
    return `&:hover {
    disabled: true;
    background: ${COLORS.primary.darkBlue};
    color: ${COLORS.primary.offWhite};
    border-left: 3px solid ${COLORS.primary.lightBlue};
    cursor: pointer;
  `
  }}
`

const SidebarLabel = styled.span<{ active?: number }>`
  margin-left: 16px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '800' : '400')};
`

const DropdownLink = styled(Link)<{ active?: number }>`
  height: 35px;
  padding-left: 2.3rem;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #f5f5f5;
  font-size: 18px;
  background: ${({ active }) => (active ? '#1DA1F2' : '#414757')};

  &:hover {
    background: #1da1f2;
    cursor: pointer;
  }
`

export const SubMenu: React.FC<SubMenuProps> = ({
  item,
  currentUser,
  showSidebar,
  setShowSidebar,
  subnav,
  setSubnav,
  onClick,
  hideBusinessEmployeeNav = false,
  pendingChanges,
  setExitModal,
}) => {
  const location = useLocation()

  const showSubnav = () => setSubnav?.(subnav === item.title ? null : item.title ?? null)
  const [hovering, setHovering] = useState(false)

  useEffect(() => {
    if (subnav) {
      setShowSidebar?.(true)
    }
  }, [subnav, setShowSidebar])

  useEffect(() => {
    if (!showSidebar) {
      setSubnav?.(null)
    }
  }, [showSidebar, setSubnav])

  const Icon = React.cloneElement(item.icon as React.ReactElement, {
    style: {
      ...(item.icon as React.ReactElement)?.props?.style,
      color:
        item?.path === location.pathname || hovering
          ? COLORS.primary.neutral
          : COLORS.primary.darkBlue,
    },
  })

  const disabled = hideBusinessEmployeeNav && item.path !== Links.BusinessDashboard
  const isActive =
    item?.path === location.pathname ||
    (item.detailedNav && item.detailedNav.find((x) => x.path === location.pathname))
      ? 1
      : 0

  // react-router v7 resolves to="#" to the CURRENT pathname — an inert "#"
  // anchor is impossible, so non-navigating entries render as a span instead.
  const linkTarget = item.path && !disabled && !pendingChanges ? item.path : null

  return (
    <>
      <SidebarLink
        as={linkTarget ? undefined : 'span'}
        {...(linkTarget ? { to: linkTarget } : {})}
        disabled={disabled}
        onClick={() => {
          if (pendingChanges) {
            if (item.path) {
              setExitModal?.(item.path)
            }
            return
          }
          if (disabled) return
          if (item.subNav) {
            showSubnav()
          } else {
            onClick?.()
          }
        }}
        active={isActive}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <span>{Icon}</span>
          {showSidebar && <SidebarLabel active={isActive}>{item.title}</SidebarLabel>}
        </View>
        <View>
          {item.subNav && subnav === item.title
            ? item.iconOpened
            : item.subNav
              ? item.iconClosed
              : null}
        </View>
      </SidebarLink>
      {subnav === item.title &&
        item.subNav?.map((subItem, index) => {
          return (
            <DropdownLink
              as={subItem.path ? undefined : 'span'}
              {...(subItem.path ? { to: subItem.path } : {})}
              key={index}
              active={subItem?.path === location.pathname ? 1 : 0}
            >
              {subItem.icon}
              <SidebarLabel>{subItem.title}</SidebarLabel>
            </DropdownLink>
          )
        })}
    </>
  )
}
